import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { describeError } from '../lib/describeError';
import { UploadUserError, validateUpload } from '../lib/validateUpload';
import { S3UploadError, uploadFileToS3 } from '../lib/uploadToS3';
import { ApiError, beginUpload, finalizeUpload } from './client';
import { keys } from './keys';

/**
 * The Drop 8 Files-rail upload flow (design doc "Error handling"), Phases
 * 1–3 chained behind one mutation: begin → POST to S3 → finalize.
 * `validateUpload` runs first and, on rejection, throws before any request is
 * sent — {@link UploadUserError} so the crafted message reaches the UI
 * verbatim instead of `describeError`'s generic-Error fallback.
 *
 * Two retries are baked in, matching the design doc exactly:
 *   - S3 `403` (size/type/tag/key mismatch, or an expired policy) — nothing
 *     was stored, so the SPA re-drives `begin` once for a fresh policy and
 *     retries the S3 POST once.
 *   - Agent `415` at finalize (magic-byte/UTF-8 check failed) — finalize is
 *     retried once, then gives up with a fixed message.
 * Every other failure (BFF `415`/`413` at begin, any other finalize error)
 * surfaces immediately, no retry.
 *
 * ponytail: no cancellation wired — `uploadFileToS3`'s `signal` param goes
 * unused, so switching workstreams mid-upload leaves it running orphaned
 * (wasted bandwidth, a stale `invalidateQueries`). Add an `AbortController`
 * tied to unmount if that turns out to matter in practice.
 */
export function useUploadArtifact(workstreamId: string | null) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState(0);
  const [filename, setFilename] = useState<string | null>(null);

  const mutation = useMutation({
    mutationKey: keys.workstream.uploadArtifact(workstreamId ?? ''),
    mutationFn: async (file: File) => {
      if (workstreamId === null) throw new Error('no active workstream');
      const validated = validateUpload(file);
      if (!validated.ok) throw new UploadUserError(validated.message);

      const begin = () =>
        beginUpload(
          workstreamId,
          { filename: file.name, contentType: validated.contentType },
          { getAccessToken },
        );

      let begun = await begin();
      try {
        await uploadFileToS3(begun.data.url, begun.data.fields, file, setProgress);
      } catch (err) {
        if (err instanceof S3UploadError && err.status === 403) {
          begun = await begin();
          await uploadFileToS3(begun.data.url, begun.data.fields, file, setProgress);
        } else {
          throw err;
        }
      }

      const finalize = () =>
        finalizeUpload(
          workstreamId,
          begun.data.artifactId,
          { filename: file.name },
          { getAccessToken },
        );

      try {
        return await finalize();
      } catch (err) {
        if (err instanceof ApiError && err.status === 415) {
          try {
            return await finalize();
          } catch {
            throw new UploadUserError('Upload failed — try again.');
          }
        }
        throw err;
      }
    },
    onMutate: (file) => {
      setFilename(file.name);
      setProgress(0);
    },
    onSettled: () => {
      setFilename(null);
      setProgress(0);
      if (workstreamId !== null) {
        void queryClient.invalidateQueries({ queryKey: keys.workstream.artifacts(workstreamId) });
      }
    },
  });

  return {
    uploadFile: (file: File) => {
      mutation.reset();
      mutation.mutate(file);
    },
    isUploading: mutation.isPending,
    /** 0–100, meaningful only while {@link isUploading} (Phase 2's XHR progress). */
    progress,
    /** The file currently uploading, for the rail's progress row label. */
    filename,
    error: mutation.error
      ? mutation.error instanceof UploadUserError
        ? mutation.error.message
        : describeError(mutation.error).userMessage
      : null,
  };
}
